import { init, type IVWOClient } from 'vwo-fme-node-sdk';

import type { VWODecisionData, VWOLogger } from './types';

/**
 * In-memory store for decision data from VWO's integrations callback.
 * Keyed by `userId:featureKey`. Entries are consumed (deleted) after reading.
 * Capped at MAX_DECISION_STORE_SIZE to prevent unbounded growth in long-lived
 * server processes (e.g., if entries are never consumed due to errors).
 */
const decisionStore = new Map<string, VWODecisionData>();
const MAX_DECISION_STORE_SIZE = 10_000;

const asString = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);
const asNumber = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

export function getDecisionData(userId: string, featureKey: string): VWODecisionData | undefined {
	const key = `${userId}:${featureKey}`;
	const data = decisionStore.get(key);
	decisionStore.delete(key);
	return data;
}

/** Cache of in-flight/initialized VWO clients, keyed by `accountId:sdkKey` */
const clientCache = new Map<string, Promise<IVWOClient>>();

/**
 * Initializes (or returns a cached) VWO FME client.
 *
 * The client polls for settings changes every 60 seconds and
 * captures experiment decision data via the integrations callback.
 *
 * @param accountId - VWO account ID
 * @param sdkKey - VWO SDK key (environment-specific)
 * @param logger - Optional logger for initialization events
 */
export async function getVWOClient(accountId: string, sdkKey: string, logger?: VWOLogger): Promise<IVWOClient> {
	if (!accountId || !sdkKey) {
		throw new Error('VWO accountId and sdkKey are required. Check your configuration.');
	}

	const cacheKey = `${accountId}:${sdkKey}`;
	const cached = clientCache.get(cacheKey);
	if (cached) return cached;

	// Cache the in-flight promise so concurrent requests don't spawn duplicate SDK instances (each starts its own settings poller).
	const clientPromise = init({
		accountId,
		sdkKey,
		pollInterval: 60_000,
		integrations: {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any -- VWO SDK callback provides untyped properties
			callback(properties: Record<string, unknown>) {
				const userId = asString(properties['userId']);
				const featureKey = asString(properties['featureKey']);
				if (userId && featureKey) {
					// Evict oldest entries if the store is at capacity
					if (decisionStore.size >= MAX_DECISION_STORE_SIZE) {
						const firstKey = decisionStore.keys().next().value;
						if (firstKey) decisionStore.delete(firstKey);
					}
					decisionStore.set(`${userId}:${featureKey}`, {
						featureKey,
						experimentId: asNumber(properties['experimentId']),
						experimentKey: asString(properties['experimentKey']),
						experimentVariationId: asNumber(properties['experimentVariationId']),
						rolloutId: asNumber(properties['rolloutId']),
						rolloutKey: asString(properties['rolloutKey']),
						rolloutVariationId: asNumber(properties['rolloutVariationId']),
					});
				}
			},
		},
	});

	clientCache.set(cacheKey, clientPromise);

	let client: IVWOClient;
	try {
		client = await clientPromise;
	} catch (error) {
		clientCache.delete(cacheKey);
		throw error;
	}

	const settings = client.settings as unknown as Record<string, unknown> | undefined;
	const features = (settings?.['features'] ?? []) as Array<Record<string, unknown>>;
	const featureKeys = features.map((f) => (f['key'] as string) ?? (f['name'] as string));

	logger?.info?.('VWO FME client initialized', {
		accountId,
		availableFeatureKeys: featureKeys,
		settingsValid: client.isSettingsValid,
	});

	return client;
}

/**
 * Clears the client cache. Useful for testing or when credentials change.
 */
export function clearVWOClientCache(): void {
	clientCache.clear();
}
